/**
 * ScenarioEnvelope — Renders prewarroom_scenarios list.
 *
 * Exploratory only — no persistence.
 * Displays baseline/conservative/aggressive scenario envelope for War Room planning.
 */

import React, { useState } from 'react';
import { Target, TrendingUp, TrendingDown, Minus, ChevronRight, AlertCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { FinancialValue } from '../shared/FinancialValue';
import { StatusBadge } from '../shared/StatusBadge';
import { PrewarroomScenariosResult, Scenario } from '../../hooks/useCopilot';

interface ScenarioEnvelopeProps {
  result: PrewarroomScenariosResult | null;
  isLoading: boolean;
}

/**
 * Get icon and color for scenario type
 */
function getScenarioStyle(type: Scenario['scenario_type']) {
  switch (type) {
    case 'baseline':
      return {
        icon: <Minus className="w-4 h-4" />,
        color: 'var(--color-text-secondary)',
        bgColor: 'var(--color-bg-elevated)',
        label: 'Baseline',
      };
    case 'conservative':
      return {
        icon: <TrendingDown className="w-4 h-4" />,
        color: 'var(--color-text-muted)',
        bgColor: 'rgba(136, 136, 136, 0.12)',
        label: 'Conservative',
      };
    case 'aggressive':
      return {
        icon: <TrendingUp className="w-4 h-4" />,
        color: 'var(--color-brand)',
        bgColor: 'var(--color-needs-human-tint)',
        label: 'Aggressive',
      };
    case 'custom':
    default:
      return {
        icon: <Target className="w-4 h-4" />,
        color: 'var(--color-text-secondary)',
        bgColor: 'var(--color-bg-elevated)',
        label: 'Custom',
      };
  }
}

/**
 * Format override summary
 */
function formatOverrides(overrides: Record<string, unknown>): string {
  const keys = Object.keys(overrides);
  if (keys.length === 0) return 'No overrides';
  if (keys.length === 1) return keys[0].replace(/_/g, ' ');
  return `${keys[0].replace(/_/g, ' ')} +${keys.length - 1} more`;
}

export function ScenarioEnvelope({ result, isLoading }: ScenarioEnvelopeProps) {
  const [selectedScenario, setSelectedScenario] = useState<Scenario | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="scenario-loading">
        <div className="flex flex-col items-center gap-3">
          <div
            className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
            style={{ borderColor: 'var(--color-brand)', borderTopColor: 'transparent' }}
          />
          <span className="text-sm text-[var(--color-text-muted)]">
            Loading scenarios...
          </span>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="py-8 text-center" data-testid="scenario-empty">
        <div className="text-[var(--color-text-muted)]">
          <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No scenarios available</p>
          <p className="text-xs mt-1 max-w-[200px] mx-auto">
            Click Refresh to generate scenario envelope
          </p>
        </div>
      </div>
    );
  }

  if (!result.baseline_available) {
    return (
      <div className="space-y-4" data-testid="scenario-no-baseline">
        <div
          className="p-4 rounded-md text-sm"
          style={{
            backgroundColor: 'rgba(255, 102, 0, 0.12)',
            border: '1px solid rgba(255, 102, 0, 0.30)',
            color: 'var(--color-brand)',
          }}
        >
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium">Baseline Not Available</div>
              <div className="opacity-90 mt-1">
                {result.note || 'The claim has no computed amount yet. Run engines first.'}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const scenarios = result.scenarios || [];
  const baselineScenario = scenarios.find((s) => s.scenario_type === 'baseline');
  const baselineTotal = result.baseline_total || baselineScenario?.projected_total;

  return (
    <div className="space-y-4" data-testid="scenario-envelope">
      {/* Baseline Summary */}
      {baselineTotal !== undefined && (
        <div className="p-4 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
          <div className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide mb-1">
            Baseline Total
          </div>
          <FinancialValue value={baselineTotal} size="xl" />
          <div className="mt-2 text-xs text-[var(--color-text-dim)]">
            Generated {new Date(result.generated_at).toLocaleString()}
          </div>
        </div>
      )}

      {/* Scenarios List */}
      <div className="space-y-2">
        <h4 className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-label)]">
          Scenario Envelope
        </h4>

        {scenarios.length === 0 ? (
          <div className="py-6 text-center text-sm text-[var(--color-text-muted)]">
            No scenarios generated yet
          </div>
        ) : (
          <div className="space-y-2">
            {scenarios.map((scenario) => {
              const style = getScenarioStyle(scenario.scenario_type);
              const isSelected = selectedScenario?.id === scenario.id;
              const diffFromBaseline = scenario.diff_vs_baseline;

              return (
                <button
                  key={scenario.id}
                  onClick={() => setSelectedScenario(isSelected ? null : scenario)}
                  className={cn(
                    'w-full text-left p-3 rounded-md border transition-all',
                    isSelected
                      ? 'border-[var(--color-brand)] bg-[var(--color-needs-human-tint)]'
                      : 'border-[var(--color-border)] bg-[var(--color-bg-elevated)] hover:border-[var(--color-border-sub)]'
                  )}
                  data-testid={`scenario-${scenario.scenario_type}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <span
                        className="p-1.5 rounded-md"
                        style={{ color: style.color, backgroundColor: style.bgColor }}
                      >
                        {style.icon}
                      </span>
                      <div>
                        <div className="text-sm font-medium text-[var(--color-text-primary)]">
                          {scenario.name}
                        </div>
                        <div className="text-xs text-[var(--color-text-muted)]">
                          {formatOverrides(scenario.overrides)}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {diffFromBaseline !== undefined && diffFromBaseline !== 0 && (
                        <span
                          className={cn(
                            'text-xs font-mono',
                            diffFromBaseline > 0
                              ? 'text-[var(--color-verified)]'
                              : 'text-[var(--color-text-muted)]'
                          )}
                        >
                          {diffFromBaseline > 0 ? '+' : ''}
                          {((diffFromBaseline / (baselineTotal || 1)) * 100).toFixed(1)}%
                        </span>
                      )}
                      <ChevronRight
                        className={cn(
                          'w-4 h-4 text-[var(--color-text-muted)] transition-transform',
                          isSelected && 'rotate-90'
                        )}
                      />
                    </div>
                  </div>

                  {/* Projected Total */}
                  {scenario.projected_total !== undefined && (
                    <div className="mt-3 pt-3 border-t border-[var(--color-border-sub)]">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-[var(--color-text-muted)]">
                          Projected
                        </span>
                        <FinancialValue value={scenario.projected_total} size="lg" />
                      </div>

                      {diffFromBaseline !== undefined && (
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-[var(--color-text-muted)]">
                            vs Baseline
                          </span>
                          <FinancialValue value={diffFromBaseline} size="sm" />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Expanded Details */}
                  {isSelected && Object.keys(scenario.overrides).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[var(--color-border-sub)]">
                      <div className="text-xs font-medium text-[var(--color-text-secondary)] mb-2">
                        Overrides
                      </div>
                      <div className="space-y-1">
                        {Object.entries(scenario.overrides).map(([key, value]) => (
                          <div
                            key={key}
                            className="flex justify-between items-center text-xs"
                          >
                            <span className="text-[var(--color-text-muted)]">
                              {key.replace(/_/g, ' ')}
                            </span>
                            <span className="font-mono text-[var(--color-text-primary)]">
                              {typeof value === 'number' ? value.toLocaleString() : String(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Note */}
      {result.note && (
        <div className="text-xs text-[var(--color-text-muted)] italic">
          {result.note}
        </div>
      )}
    </div>
  );
}

export default ScenarioEnvelope;
